/**
 * Online Radio UI: meta on load, autoplay when a station is selected.
 */ /**
 * PCM playback: AudioWorklet on HTTPS, queue on HTTP (LAN without secure context).
 */ /** Max queued samples (~5 s stereo @ 48 kHz). */ const $c48bc58cad3b90b9$var$MAX_QUEUE_SAMPLES = 480000;
/** Fallback schedules audio ahead to avoid gaps between 60 ms chunks. */ const $c48bc58cad3b90b9$var$FALLBACK_TARGET_AHEAD_SEC = 0.30;
/** Prevent fallback from drifting too far ahead of live on burst receive. */ const $c48bc58cad3b90b9$var$FALLBACK_MAX_AHEAD_SEC = 0.60;
/** Small lead for the first AudioBufferSource after start/flush. */ const $c48bc58cad3b90b9$var$FALLBACK_START_LEAD_SEC = 0.08;
var $c177b35fb302298f$exports = {};
$c177b35fb302298f$exports = import.meta.resolve("iKmDM");


async function $c48bc58cad3b90b9$export$19feba75ce3f5ab9(ctx, onUnderrun = ()=>{}, onReady = ()=>{}) {
    if (ctx.audioWorklet) {
        await ctx.audioWorklet.addModule(new URL($c177b35fb302298f$exports));
        const node = new AudioWorkletNode(ctx, "pcm-processor", {
            numberOfInputs: 0,
            numberOfOutputs: 1,
            outputChannelCount: [
                2
            ],
            channelCount: 2,
            channelCountMode: "explicit",
            channelInterpretation: "speakers"
        });
        node.connect(ctx.destination);
        node.port.onmessage = (event)=>{
            if (event.data?.type === "underrun") onUnderrun(event.data.count);
            else if (event.data?.type === "workletReady") onReady(event.data.sampleRate);
        };
        return {
            mode: "worklet",
            setPause (value) {
                node.port.postMessage({
                    type: "pause",
                    value: value
                });
            },
            setMute (value) {
                node.port.postMessage({
                    type: "mute",
                    value: value
                });
            },
            setGain (value) {
                node.port.postMessage({
                    type: "gain",
                    value: value
                });
            },
            pushPcm (samples, channels = 1) {
                const copy = new Float32Array(samples);
                node.port.postMessage({
                    type: "pcm",
                    samples: copy,
                    channels: channels
                }, [
                    copy.buffer
                ]);
            },
            flush () {
                node.port.postMessage({
                    type: "flush"
                });
            }
        };
    }
    return $c48bc58cad3b90b9$var$createBufferPlayer(ctx, onReady);
}
/**
 * Fallback for browsers without AudioWorklet.
 *
 * BufferSource is one-shot, so we schedule several short buffers ahead.
 * Do not wait for the previous 60-ms chunk onended: by then the next start
 * would be in the past and silence appears between frames.
 * @param {AudioContext} ctx
 * @param {(sampleRate: number) => void} [onReady]
 */ function $c48bc58cad3b90b9$var$createBufferPlayer(ctx, onReady = ()=>{}) {
    onReady(ctx.sampleRate);
    const gainNode = ctx.createGain();
    gainNode.connect(ctx.destination);
    let paused = false;
    let muted = false;
    let gain = 0.8;
    /** @type {Float32Array[]} */ let queue = [];
    let queueSamples = 0;
    let scheduledUntil = 0;
    /** @type {Set<AudioBufferSourceNode>} */ let active = new Set();
    let scheduling = false;
    gainNode.gain.value = gain;
    /** Schedules PCM up to target lead, keeping a continuous timeline. */ function scheduleAhead() {
        if (scheduling || paused || muted || queue.length === 0) return;
        scheduling = true;
        try {
            const now = ctx.currentTime;
            // After start/flush or main-thread delay, start slightly ahead of now.
            // Otherwise AudioContext rejects start in the past.
            if (scheduledUntil < now + $c48bc58cad3b90b9$var$FALLBACK_START_LEAD_SEC) scheduledUntil = now + $c48bc58cad3b90b9$var$FALLBACK_START_LEAD_SEC;
            // Schedule about 300 ms. Upper bound guards against burst receive:
            // latency matters more than keeping old radio chunks.
            const targetUntil = now + $c48bc58cad3b90b9$var$FALLBACK_TARGET_AHEAD_SEC;
            const maxUntil = now + $c48bc58cad3b90b9$var$FALLBACK_MAX_AHEAD_SEC;
            while(queue.length > 0 && scheduledUntil < targetUntil && scheduledUntil < maxUntil){
                const item = queue.shift();
                if (!item) break;
                const samples = item.samples;
                const ch = item.channels || 1;
                queueSamples -= samples.length;
                const frames = ch > 1 ? samples.length / ch : samples.length;
                const buffer = ctx.createBuffer(ch, frames, ctx.sampleRate);
                if (ch === 1) buffer.copyToChannel(samples, 0);
                else {
                    const left = new Float32Array(frames);
                    const right = new Float32Array(frames);
                    for(let i = 0; i < frames; i += 1){
                        left[i] = samples[i * 2];
                        right[i] = samples[i * 2 + 1];
                    }
                    buffer.copyToChannel(left, 0);
                    buffer.copyToChannel(right, 1);
                }
                const source = ctx.createBufferSource();
                source.buffer = buffer;
                source.connect(gainNode);
                active.add(source);
                source.start(scheduledUntil);
                scheduledUntil += buffer.duration;
                source.onended = ()=>{
                    active.delete(source);
                    scheduleAhead();
                };
            }
        } finally{
            scheduling = false;
        }
    }
    return {
        mode: "buffer",
        setPause (value) {
            paused = value;
            if (paused) {
                for (const source of active)try {
                    source.stop();
                } catch  {
                /* already stopped */ }
                active.clear();
                scheduledUntil = 0;
            } else scheduleAhead();
        },
        setMute (value) {
            muted = value;
            gainNode.gain.value = muted ? 0 : gain;
        },
        setGain (value) {
            gain = value;
            if (!muted) gainNode.gain.value = gain;
        },
        pushPcm (samples, channels = 1) {
            if (paused || muted) return;
            const copy = new Float32Array(samples);
            queue.push({
                samples: copy,
                channels: channels
            });
            queueSamples += copy.length;
            while(queueSamples > $c48bc58cad3b90b9$var$MAX_QUEUE_SAMPLES && queue.length > 0){
                const dropped = queue.shift();
                if (dropped) queueSamples -= dropped.samples.length;
            }
            scheduleAhead();
        },
        flush () {
            queue = [];
            queueSamples = 0;
            scheduledUntil = 0;
            for (const source of active)try {
                source.stop();
            } catch  {
            /* ok */ }
            active.clear();
        }
    };
}


/** WS PING interval for link quality (seconds). */ const $4a68f02554b6f92a$export$3ddc3df169b908 = 3;
const $4a68f02554b6f92a$export$35c5df7872f016f3 = 3000;
function $4a68f02554b6f92a$export$b27848052134e8d9(builder) {
    return builder.pingIntervalSec($4a68f02554b6f92a$export$3ddc3df169b908);
}
function $4a68f02554b6f92a$export$22b6e3e09e7618e1(snapshot) {
    const last = snapshot?.lastRttMs;
    const median = snapshot?.medianRttMs;
    if (last == null && median == null) return "ws rtt \u2026";
    const parts = [];
    if (last != null) parts.push(`last ${last} ms`);
    if (median != null) parts.push(`median ${median} ms`);
    return `ws ${parts.join(", ")}`;
}
function $4a68f02554b6f92a$export$9da51ec074aeac90(client) {
    if (!client || typeof client.linkQuality !== "function") return {
        lastRttMs: undefined,
        medianRttMs: undefined
    };
    return client.linkQuality(30);
}
function $4a68f02554b6f92a$export$f16c22ed31b708df(client, onSnapshot, intervalMs = $4a68f02554b6f92a$export$35c5df7872f016f3) {
    const tick = ()=>onSnapshot($4a68f02554b6f92a$export$9da51ec074aeac90(client));
    tick();
    return window.setInterval(tick, intervalMs);
}


/** @type {Worker|null} */ let $cfca9a91faa35995$var$worker = null;
/** @type {AudioContext|null} */ let $cfca9a91faa35995$var$audioContext = null;
/** @type {Awaited<ReturnType<typeof createPcmOutput>>|null} */ let $cfca9a91faa35995$var$pcmOut = null;
let $cfca9a91faa35995$var$metaConnected = false;
let $cfca9a91faa35995$var$audioStarted = false;
let $cfca9a91faa35995$var$paused = false;
let $cfca9a91faa35995$var$muted = false;
/** AudioWorklet starvation is local output trouble, not a FastPubSub packet loss. */ let $cfca9a91faa35995$var$audioOutputUnderruns = 0;
let $cfca9a91faa35995$var$metaSampleRate = 48000;
/** Stream parameters from meta snapshot. */ let $cfca9a91faa35995$var$wireMeta = {
    sampleRate: 48000,
    frameMs: 60,
    channels: 2,
    bitrate: 128000
};
let $cfca9a91faa35995$var$stations = [];
let $cfca9a91faa35995$var$selectedStationId = null;
let $cfca9a91faa35995$var$pendingProtectedStation = null;
/** Passwords for protected stations in this browser tab session. */ const $cfca9a91faa35995$var$stationPasswords = new Map();
/** Start playback after station pick (including ?station=). */ let $cfca9a91faa35995$var$pendingAutoplay = false;
let $cfca9a91faa35995$var$autoplayInFlight = false;
/** songId and duration for progress bar */ let $cfca9a91faa35995$var$progressSongId = 0;
let $cfca9a91faa35995$var$progressDurationMs = 0;
/** Last known output channel count (workletReady must not reset this to 1). */ let $cfca9a91faa35995$var$lastOutChannels = 2;
let $cfca9a91faa35995$var$displayedSongId = 0;
const $cfca9a91faa35995$var$statusEl = document.getElementById("status");
const $cfca9a91faa35995$var$btnPlay = document.getElementById("btn-play");
const $cfca9a91faa35995$var$btnPause = document.getElementById("btn-pause");
const $cfca9a91faa35995$var$btnMute = document.getElementById("btn-mute");
const $cfca9a91faa35995$var$volumeEl = document.getElementById("volume");
const $cfca9a91faa35995$var$stationListEl = document.getElementById("station-list");
const $cfca9a91faa35995$var$coverEl = document.getElementById("cover");
const $cfca9a91faa35995$var$coverPlaceholder = document.getElementById("cover-placeholder");
const $cfca9a91faa35995$var$titleEl = document.getElementById("track-title");
const $cfca9a91faa35995$var$artistEl = document.getElementById("track-artist");
const $cfca9a91faa35995$var$streamInfoEl = document.getElementById("stream-info");
const $cfca9a91faa35995$var$progressBar = document.getElementById("progress-bar");
const $cfca9a91faa35995$var$statEdge = document.getElementById("stat-edge");
const $cfca9a91faa35995$var$statWsRtt = document.getElementById("stat-ws-rtt");
const $cfca9a91faa35995$var$statFps = document.getElementById("stat-fps");
const $cfca9a91faa35995$var$statTenant = document.getElementById("stat-tenant");
const $cfca9a91faa35995$var$statMetaCh = document.getElementById("stat-meta-ch");
const $cfca9a91faa35995$var$statAudioCh = document.getElementById("stat-audio-ch");
const $cfca9a91faa35995$var$statWire = document.getElementById("stat-wire");
const $cfca9a91faa35995$var$statAudioOut = document.getElementById("stat-audio-out");
const $cfca9a91faa35995$var$statBuffer = document.getElementById("stat-buffer");
const $cfca9a91faa35995$var$statSong = document.getElementById("stat-song");
const $cfca9a91faa35995$var$statRecv = document.getElementById("stat-recv");
const $cfca9a91faa35995$var$statPos = document.getElementById("stat-pos");
const $cfca9a91faa35995$var$statLost = document.getElementById("stat-lost");
const $cfca9a91faa35995$var$statGaps = document.getElementById("stat-gaps");
const $cfca9a91faa35995$var$statDecode = document.getElementById("stat-decode");
const $cfca9a91faa35995$var$statDecodeLate = document.getElementById("stat-decode-late");
const $cfca9a91faa35995$var$statFec = document.getElementById("stat-fec");
const $cfca9a91faa35995$var$statDup = document.getElementById("stat-dup");
const $cfca9a91faa35995$var$statOld = document.getElementById("stat-old");
const $cfca9a91faa35995$var$statParse = document.getElementById("stat-parse");
const $cfca9a91faa35995$var$statEvict = document.getElementById("stat-evict");
const $cfca9a91faa35995$var$statSilence = document.getElementById("stat-silence");
const $cfca9a91faa35995$var$statDecoded = document.getElementById("stat-decoded");
const $cfca9a91faa35995$var$statSdkQueue = document.getElementById("stat-sdk-queue");
const $cfca9a91faa35995$var$statSdkEvents = document.getElementById("stat-sdk-events");
const $cfca9a91faa35995$var$statDecodeOrder = document.getElementById("stat-decode-order");
const $cfca9a91faa35995$var$statAudioUnderrun = document.getElementById("stat-audio-underrun");
const $cfca9a91faa35995$var$statLastDiagnostic = document.getElementById("stat-last-diagnostic");
const $cfca9a91faa35995$var$fpsStatusEl = document.getElementById("fps-status");
const $cfca9a91faa35995$var$fpsLabelEl = document.getElementById("fps-label");
let $cfca9a91faa35995$var$fpsConnectionState = "connecting";
let $cfca9a91faa35995$var$fpsConnectionDetail = "connecting\u2026";
let $cfca9a91faa35995$var$linkQualityLabel = "ws rtt \u2026";
/** Selected edge from worker, e.g. `edge-fra-01 (12 ms)`. */ let $cfca9a91faa35995$var$selectedEdgeLabel = "";
const $cfca9a91faa35995$var$passwordDialog = document.getElementById("password-dialog");
const $cfca9a91faa35995$var$passwordForm = document.getElementById("password-form");
const $cfca9a91faa35995$var$passwordHint = document.getElementById("password-hint");
const $cfca9a91faa35995$var$passwordInput = document.getElementById("password-input");
$cfca9a91faa35995$var$btnPlay.addEventListener("click", $cfca9a91faa35995$var$onPlay);
$cfca9a91faa35995$var$btnPause.addEventListener("click", $cfca9a91faa35995$var$onPause);
$cfca9a91faa35995$var$btnMute.addEventListener("click", $cfca9a91faa35995$var$onMute);
$cfca9a91faa35995$var$volumeEl.addEventListener("input", $cfca9a91faa35995$var$onVolume);
$cfca9a91faa35995$var$passwordForm.addEventListener("submit", $cfca9a91faa35995$var$onPasswordSubmit);
var $9973e3831731285e$exports = {};
var $56b621a6dd25b2cb$exports = {};
"use strict";
$56b621a6dd25b2cb$exports = function(workerUrl, origin, isESM) {
    if (origin === self.location.origin) // If the worker bundle's url is on the same origin as the document,
    // use the worker bundle's own url.
    return workerUrl;
    else {
        // Otherwise, create a blob URL which loads the worker bundle with `importScripts`.
        var source = isESM ? 'import ' + JSON.stringify(workerUrl) + ';' : 'importScripts(' + JSON.stringify(workerUrl) + ');';
        return URL.createObjectURL(new Blob([
            source
        ], {
            type: 'application/javascript'
        }));
    }
};


let $9973e3831731285e$var$url = new URL(import.meta.resolve("9rUcY"));
$9973e3831731285e$exports = $56b621a6dd25b2cb$exports($9973e3831731285e$var$url.toString(), $9973e3831731285e$var$url.origin, true);


/**
 * On page load: worker + meta channel.
 */ function $cfca9a91faa35995$var$boot() {
    $cfca9a91faa35995$var$setFpsConnection("connecting", "connecting\u2026");
    $cfca9a91faa35995$var$setStatus("connecting to meta\u2026", true);
    $cfca9a91faa35995$var$worker = new Worker($9973e3831731285e$exports);
    $cfca9a91faa35995$var$worker.onmessage = $cfca9a91faa35995$var$onWorkerMessage;
    $cfca9a91faa35995$var$worker.onerror = (event)=>{
        $cfca9a91faa35995$var$setFpsConnection("error", "worker error");
        $cfca9a91faa35995$var$setStatus(`worker error: ${event.message}`, false);
    };
    $cfca9a91faa35995$var$worker.postMessage({
        type: "connectMeta"
    });
}
/**
 * Play: AudioContext + subscription to selected station stream.
 */ async function $cfca9a91faa35995$var$onPlay() {
    if ($cfca9a91faa35995$var$audioStarted) {
        if ($cfca9a91faa35995$var$paused) {
            $cfca9a91faa35995$var$onPause();
            return;
        }
        if ($cfca9a91faa35995$var$audioContext?.state === "suspended") await $cfca9a91faa35995$var$audioContext.resume();
        $cfca9a91faa35995$var$setStatus("playing", true);
        return;
    }
    if (!$cfca9a91faa35995$var$metaConnected) {
        $cfca9a91faa35995$var$setStatus("waiting for meta\u2026", true, "warn");
        return;
    }
    if ($cfca9a91faa35995$var$selectedStationId === null) {
        $cfca9a91faa35995$var$setStatus("select a station", true, "warn");
        return;
    }
    $cfca9a91faa35995$var$setStatus("starting audio\u2026", true);
    $cfca9a91faa35995$var$btnPlay.disabled = true;
    try {
        $cfca9a91faa35995$var$audioContext = new AudioContext({
            sampleRate: $cfca9a91faa35995$var$metaSampleRate
        });
        $cfca9a91faa35995$var$pcmOut = await (0, $c48bc58cad3b90b9$export$19feba75ce3f5ab9)($cfca9a91faa35995$var$audioContext, (count)=>{
            $cfca9a91faa35995$var$audioOutputUnderruns = count;
            $cfca9a91faa35995$var$updateAudioOutStats();
            $cfca9a91faa35995$var$updateAudioUnderrunDiagnostic();
        }, (workletHz)=>{
            const hz = Math.round(workletHz || $cfca9a91faa35995$var$audioContext.sampleRate);
            $cfca9a91faa35995$var$worker?.postMessage({
                type: "setOutputSampleRate",
                sampleRate: hz
            });
            $cfca9a91faa35995$var$updateAudioOutStats();
        });
        $cfca9a91faa35995$var$onVolume();
        $cfca9a91faa35995$var$updateAudioOutStats();
        $cfca9a91faa35995$var$worker.postMessage({
            type: "startAudio",
            outputSampleRate: $cfca9a91faa35995$var$audioContext.sampleRate
        });
        await $cfca9a91faa35995$var$audioContext.resume();
        if (Math.abs($cfca9a91faa35995$var$audioContext.sampleRate - $cfca9a91faa35995$var$metaSampleRate) > 1) console.warn(`[radio UI] AudioContext ${$cfca9a91faa35995$var$audioContext.sampleRate} Hz (requested ${$cfca9a91faa35995$var$metaSampleRate}) \u{2014} worker resample`);
        if ($cfca9a91faa35995$var$audioContext.state !== "running") throw new Error(`AudioContext not running: ${$cfca9a91faa35995$var$audioContext.state}`);
        $cfca9a91faa35995$var$audioStarted = true;
        $cfca9a91faa35995$var$btnPause.disabled = false;
        $cfca9a91faa35995$var$btnMute.disabled = false;
        if ($cfca9a91faa35995$var$pcmOut.mode === "buffer") $cfca9a91faa35995$var$setStatus("playing (HTTP fallback)", true);
    } catch (error) {
        $cfca9a91faa35995$var$audioContext?.close();
        $cfca9a91faa35995$var$audioContext = null;
        $cfca9a91faa35995$var$pcmOut = null;
        $cfca9a91faa35995$var$btnPlay.disabled = false;
        $cfca9a91faa35995$var$setFpsConnection("error", "audio");
        $cfca9a91faa35995$var$setStatus(error.message || String(error), false);
    }
}
function $cfca9a91faa35995$var$onPause() {
    if (!$cfca9a91faa35995$var$audioStarted) return;
    $cfca9a91faa35995$var$paused = !$cfca9a91faa35995$var$paused;
    if ($cfca9a91faa35995$var$paused) {
        $cfca9a91faa35995$var$worker?.postMessage({
            type: "pauseAudio"
        });
        $cfca9a91faa35995$var$pcmOut?.flush();
        $cfca9a91faa35995$var$btnPause.textContent = "Resume";
        $cfca9a91faa35995$var$setStatus("paused (stream off)", true);
    } else {
        $cfca9a91faa35995$var$worker?.postMessage({
            type: "resumeAudio"
        });
        $cfca9a91faa35995$var$pcmOut?.flush();
        $cfca9a91faa35995$var$btnPause.textContent = "Pause";
        $cfca9a91faa35995$var$setStatus("buffering\u2026", true, "warn");
    }
}
function $cfca9a91faa35995$var$onMute() {
    if (!$cfca9a91faa35995$var$audioStarted) return;
    $cfca9a91faa35995$var$muted = !$cfca9a91faa35995$var$muted;
    $cfca9a91faa35995$var$pcmOut?.setMute($cfca9a91faa35995$var$muted);
    $cfca9a91faa35995$var$btnMute.textContent = $cfca9a91faa35995$var$muted ? "Unmute" : "Mute";
}
function $cfca9a91faa35995$var$onVolume() {
    const gain = Number($cfca9a91faa35995$var$volumeEl.value) / 100;
    $cfca9a91faa35995$var$pcmOut?.setGain(gain);
}
/**
 * @param {MessageEvent} event
 */ function $cfca9a91faa35995$var$onWorkerMessage(event) {
    const msg = event.data;
    switch(msg.type){
        case "connecting":
            $cfca9a91faa35995$var$setFpsConnection("connecting", msg.reason ? "reconnect\u2026" : "connecting\u2026");
            $cfca9a91faa35995$var$setStatus(msg.reason ? `reconnect (${msg.reason})\u{2026}` : "connecting to meta\u2026", true);
            break;
        case "ready":
            $cfca9a91faa35995$var$metaConnected = true;
            $cfca9a91faa35995$var$tryAutoplay();
            if (msg.edge) $cfca9a91faa35995$var$selectedEdgeLabel = msg.edge;
            $cfca9a91faa35995$var$statEdge.textContent = $cfca9a91faa35995$var$selectedEdgeLabel || "\u2014";
            $cfca9a91faa35995$var$statTenant.textContent = msg.tenant || "\u2014";
            $cfca9a91faa35995$var$statMetaCh.textContent = msg.metaChannel ? `sub ${msg.metaChannel}` : "\u2014";
            $cfca9a91faa35995$var$statFps.textContent = "meta online";
            if (msg.tokenTtlSec) console.info(`[radio UI] AT TTL ${msg.tokenTtlSec}s`);
            if (msg.reason === "token reissue") {
                $cfca9a91faa35995$var$setFpsConnection("meta", "meta \xb7 token reissue");
                $cfca9a91faa35995$var$setStatus("\u043D\u043E\u0432\u044B\u0439 AT, meta \u043F\u0435\u0440\u0435\u043F\u043E\u0434\u043A\u043B\u044E\u0447\u0435\u043D\u0430", true);
            } else {
                $cfca9a91faa35995$var$setFpsConnection("meta", "meta \xb7 online");
                $cfca9a91faa35995$var$setStatus("meta connected \u2014 waiting for station catalog\u2026", true);
            }
            break;
        case "meta":
            $cfca9a91faa35995$var$updateWireMeta(msg.meta);
            $cfca9a91faa35995$var$renderMeta(msg.meta);
            $cfca9a91faa35995$var$refreshNowPlayingCard();
            $cfca9a91faa35995$var$updateStreamDisplay();
            $cfca9a91faa35995$var$statFps.textContent = $cfca9a91faa35995$var$audioStarted ? "meta+audio" : "meta live";
            if (!$cfca9a91faa35995$var$audioStarted) {
                const count = msg.meta.stations?.length ?? 0;
                $cfca9a91faa35995$var$setFpsConnection("meta", `meta \xb7 ${count} st`);
                if (!$cfca9a91faa35995$var$pendingAutoplay) $cfca9a91faa35995$var$setStatus("select a station", true);
                $cfca9a91faa35995$var$tryAutoplay();
            }
            break;
        case "nowPlaying":
            if (msg.station && $cfca9a91faa35995$var$selectedStationId !== null && Number(msg.station.id) === Number($cfca9a91faa35995$var$selectedStationId)) {
                $cfca9a91faa35995$var$mergeStationMeta(msg.station);
                $cfca9a91faa35995$var$updateNowPlaying(msg.station);
            }
            break;
        case "stationPicked":
            $cfca9a91faa35995$var$selectedStationId = msg.stationId;
            $cfca9a91faa35995$var$renderMeta({
                stations: $cfca9a91faa35995$var$stations
            });
            $cfca9a91faa35995$var$updateStreamDisplay();
            $cfca9a91faa35995$var$btnPlay.disabled = false;
            $cfca9a91faa35995$var$requestAutoplay();
            break;
        case "stationSelected":
            $cfca9a91faa35995$var$statAudioCh.textContent = msg.streamChannel ? `sub ${msg.streamChannel}` : "\u2014";
            $cfca9a91faa35995$var$setFpsConnection("audio", `audio \xb7 ${msg.streamChannel || "ok"}`);
            break;
        case "audioStarted":
            $cfca9a91faa35995$var$btnPlay.disabled = false;
            $cfca9a91faa35995$var$btnPlay.textContent = "Play";
            $cfca9a91faa35995$var$statAudioCh.textContent = msg.streamChannel ? `sub ${msg.streamChannel}` : "\u2014";
            $cfca9a91faa35995$var$updateAudioOutStats(msg.channels);
            $cfca9a91faa35995$var$statFps.textContent = "meta+audio";
            $cfca9a91faa35995$var$setFpsConnection("audio", `audio \xb7 ${msg.streamChannel || "ok"}`);
            $cfca9a91faa35995$var$setStatus("buffering\u2026", true, "warn");
            break;
        case "pcm":
            $cfca9a91faa35995$var$pcmOut?.pushPcm(msg.samples, msg.channels || 1);
            break;
        case "pcm_flush":
            $cfca9a91faa35995$var$pcmOut?.flush();
            break;
        case "buffering":
            if ($cfca9a91faa35995$var$audioStarted) $cfca9a91faa35995$var$setStatus("buffering\u2026", true, "warn");
            break;
        case "playing":
            $cfca9a91faa35995$var$setFpsConnection("audio", "audio \xb7 playing");
            $cfca9a91faa35995$var$setStatus("playing", true);
            break;
        case "audioPaused":
            $cfca9a91faa35995$var$statRecv.textContent = "\u2014";
            $cfca9a91faa35995$var$setFpsConnection("audio", "audio \xb7 paused");
            break;
        case "audioResumed":
            $cfca9a91faa35995$var$statAudioCh.textContent = msg.streamChannel ? `sub ${msg.streamChannel}` : "\u2014";
            $cfca9a91faa35995$var$setFpsConnection("audio", "audio \xb7 buffering");
            break;
        case "stats":
            $cfca9a91faa35995$var$renderStats(msg);
            break;
        case "link_quality":
            $cfca9a91faa35995$var$linkQualityLabel = (0, $4a68f02554b6f92a$export$22b6e3e09e7618e1)(msg);
            if ($cfca9a91faa35995$var$statWsRtt) $cfca9a91faa35995$var$statWsRtt.textContent = $cfca9a91faa35995$var$linkQualityLabel;
            $cfca9a91faa35995$var$refreshFpsHeader();
            break;
        case "transport_diag":
            $cfca9a91faa35995$var$renderStats({
                ...msg,
                lastDiagnostic: msg.message
            });
            // rttMeasured is noisy; keep real problems visible.
            if (msg.message && msg.message !== "rttMeasured") console.warn(`[radio UI] SDK diagnostic: ${msg.message}`);
            break;
        case "chunk_gap":
            $cfca9a91faa35995$var$renderStats(msg);
            console.warn(`[radio UI] gap ${msg.source}: song_id=${msg.songId} ` + `${msg.fromMs}\u{2192}${msg.toMs} ms (\u{2212}${msg.gapChunks} ch) \u{2014} ${msg.reason}`);
            break;
        case "chunk_evicted":
            $cfca9a91faa35995$var$renderStats(msg);
            console.warn(`[radio UI] evicted: ${msg.key}`);
            break;
        case "decode_error":
            $cfca9a91faa35995$var$renderStats(msg);
            console.warn(`[radio UI] decode song_id=${msg.songId} pos=${msg.positionMs}: ${msg.message}`);
            $cfca9a91faa35995$var$setStatus(`decode: ${msg.message}`, true, "warn");
            break;
        case "needPassword":
            $cfca9a91faa35995$var$pendingProtectedStation = msg.station;
            $cfca9a91faa35995$var$passwordHint.textContent = msg.station.passwordHint ? `Hint: ${msg.station.passwordHint}` : "Enter password";
            $cfca9a91faa35995$var$passwordInput.value = "";
            $cfca9a91faa35995$var$passwordDialog.showModal();
            break;
        case "decrypt_error":
            $cfca9a91faa35995$var$setFpsConnection("error", "decrypt");
            $cfca9a91faa35995$var$setStatus(msg.message || "decrypt error", false);
            break;
        case "error":
            $cfca9a91faa35995$var$setFpsConnection("error", "error");
            $cfca9a91faa35995$var$setStatus(msg.message || "error", false);
            $cfca9a91faa35995$var$btnPlay.disabled = false;
            break;
        default:
            break;
    }
}
/**
 * Shows wire → ctx Hz, channels, and whether worker resamples.
 * @param {number} [channels]
 */ function $cfca9a91faa35995$var$updateAudioOutStats(channels) {
    if (!$cfca9a91faa35995$var$audioContext || !$cfca9a91faa35995$var$pcmOut) {
        $cfca9a91faa35995$var$statAudioOut.textContent = "\u2014";
        $cfca9a91faa35995$var$statAudioOut.className = "";
        return;
    }
    const wireHz = $cfca9a91faa35995$var$metaSampleRate;
    const ctxHz = Math.round($cfca9a91faa35995$var$audioContext.sampleRate);
    if (channels) $cfca9a91faa35995$var$lastOutChannels = channels;
    const ch = $cfca9a91faa35995$var$lastOutChannels;
    const resample = Math.abs(ctxHz - wireHz) > 1;
    const mode = $cfca9a91faa35995$var$pcmOut.mode;
    $cfca9a91faa35995$var$statAudioOut.textContent = `${wireHz}\u{2192}${ctxHz} Hz \xb7 ${ch} ch \xb7 ${mode}` + (resample ? " \xb7 resample" : " \xb7 direct") + ($cfca9a91faa35995$var$audioOutputUnderruns > 0 ? ` \xb7 underrun ${$cfca9a91faa35995$var$audioOutputUnderruns}` : "");
    $cfca9a91faa35995$var$statAudioOut.className = resample || $cfca9a91faa35995$var$audioOutputUnderruns > 0 ? "warn" : "";
    $cfca9a91faa35995$var$statAudioOut.title = resample ? `Worker resample ${wireHz}\u{2192}${ctxHz} Hz (may reduce audio quality)` : $cfca9a91faa35995$var$audioOutputUnderruns > 0 ? `AudioWorklet did not receive PCM in time ${$cfca9a91faa35995$var$audioOutputUnderruns} times` : `Wire and AudioContext match (${wireHz} Hz)`;
}
/** AudioWorklet underflow is a local output failure, separate from packet loss. */ function $cfca9a91faa35995$var$updateAudioUnderrunDiagnostic() {
    $cfca9a91faa35995$var$statAudioUnderrun.textContent = String($cfca9a91faa35995$var$audioOutputUnderruns);
    $cfca9a91faa35995$var$statAudioUnderrun.className = $cfca9a91faa35995$var$audioOutputUnderruns > 0 ? "warn" : "";
    if ($cfca9a91faa35995$var$audioOutputUnderruns > 0) {
        $cfca9a91faa35995$var$statLastDiagnostic.textContent = `AudioWorklet underrun \xd7${$cfca9a91faa35995$var$audioOutputUnderruns}: output inserted silence`;
        $cfca9a91faa35995$var$statLastDiagnostic.className = "warn";
    }
}
/**
 * Formats recv/s.
 * @param {number|undefined} value
 * @param {number} [digits=1]
 * @returns {string}
 */ function $cfca9a91faa35995$var$formatRecvRate(value, digits = 1) {
    if (value === undefined || value === null || Number.isNaN(value)) return "\u2014";
    return Number(value).toFixed(digits);
}
/**
 * Highlights dropped-message counter.
 * @param {HTMLElement} el
 * @param {number} value
 */ function $cfca9a91faa35995$var$renderDropStat(el, value) {
    el.textContent = String(value);
    el.className = value > 0 ? "warn" : "";
}
/**
 * Updates stats block (including chunk losses).
 * @param {object} msg
 */ function $cfca9a91faa35995$var$renderStats(msg) {
    if (msg.bufferMs !== undefined || msg.queuedMs !== undefined) {
        const cont = msg.bufferMs ?? 0;
        const pcmReady = msg.pcmReadyMs ?? 0;
        const queued = msg.queuedMs ?? 0;
        const chunks = msg.queuedChunks ?? 0;
        const lag = msg.lagMs ?? 0;
        $cfca9a91faa35995$var$statBuffer.textContent = `${cont} ms \xb7 pcm ${pcmReady} ms \xb7 q ${queued} ms \xb7 lag ${lag} ms (${chunks} ch)`;
        $cfca9a91faa35995$var$statBuffer.className = lag > 800 || pcmReady < 120 ? "warn" : "";
    }
    if (msg.songId !== undefined) $cfca9a91faa35995$var$statSong.textContent = String(msg.songId);
    if (msg.recvPerSecRecent !== undefined || msg.recvPerSecSong !== undefined) {
        const recent = $cfca9a91faa35995$var$formatRecvRate(msg.recvPerSecRecent);
        const song = $cfca9a91faa35995$var$formatRecvRate(msg.recvPerSecSong, 2);
        const raw = $cfca9a91faa35995$var$formatRecvRate(msg.recvPerSecRaw);
        const idleMs = msg.recvIdleMs ?? 0;
        const skipped = (msg.recvBadParse ?? 0) + (msg.recvOldSong ?? 0);
        let line = `${recent} \xb7 ${song}`;
        const rawNum = msg.recvPerSecRaw ?? 0;
        const recentNum = msg.recvPerSecRecent ?? 0;
        if (rawNum - recentNum > 0.4) line += ` \xb7 raw ${raw}`;
        if (idleMs > 80) {
            line += ` (+${Math.round(idleMs)} ms)`;
            $cfca9a91faa35995$var$statRecv.className = "warn";
        } else $cfca9a91faa35995$var$statRecv.className = skipped > 0 ? "warn" : "";
        $cfca9a91faa35995$var$statRecv.textContent = line;
        $cfca9a91faa35995$var$statRecv.title = "chunks/s over 1 s \xb7 track average \xb7 raw WS/s";
    }
    if (msg.positionMs !== undefined) {
        const chunk = msg.chunkIndex !== undefined ? `#${msg.chunkIndex}` : "";
        $cfca9a91faa35995$var$statPos.textContent = chunk ? `${msg.positionMs} ms \xb7 ${chunk}` : `${msg.positionMs} ms`;
        if ($cfca9a91faa35995$var$audioStarted && $cfca9a91faa35995$var$selectedStationId !== null) {
            let durationMs = $cfca9a91faa35995$var$progressDurationMs;
            if (durationMs <= 0 && msg.maxRecvMs > 0) durationMs = msg.maxRecvMs + $cfca9a91faa35995$var$wireMeta.frameMs;
            $cfca9a91faa35995$var$updateProgressBar(msg.positionMs, durationMs, msg.songId ?? $cfca9a91faa35995$var$progressSongId);
        }
    }
    const lost = msg.lostChunks ?? 0;
    const lostMs = msg.lostMs ?? 0;
    $cfca9a91faa35995$var$statLost.textContent = lost > 0 ? `${lost} (\u{2212}${lostMs} ms)` : "0";
    $cfca9a91faa35995$var$statLost.className = lost > 0 ? "warn" : "";
    const recvGaps = msg.recvGaps ?? 0;
    const playGaps = msg.playGaps ?? 0;
    const recvReorder = msg.recvReorder ?? 0;
    $cfca9a91faa35995$var$statGaps.textContent = `recv ${recvGaps} / play ${playGaps} / late ${recvReorder}`;
    $cfca9a91faa35995$var$statGaps.className = recvGaps + playGaps > 0 ? "warn" : "";
    const decodeErr = msg.decodeErrors ?? 0;
    $cfca9a91faa35995$var$statDecode.textContent = String(decodeErr);
    $cfca9a91faa35995$var$statDecode.className = decodeErr > 0 ? "warn" : "";
    $cfca9a91faa35995$var$renderDropStat($cfca9a91faa35995$var$statDecodeLate, msg.decodeLate ?? 0);
    $cfca9a91faa35995$var$renderDropStat($cfca9a91faa35995$var$statFec, msg.fecRecover ?? 0);
    const decoded = msg.decodedFrames ?? 0;
    $cfca9a91faa35995$var$statDecoded.textContent = String(decoded);
    const silence = msg.silenceFrames ?? 0;
    $cfca9a91faa35995$var$statSilence.textContent = String(silence);
    $cfca9a91faa35995$var$statSilence.className = silence > 0 ? "warn" : "";
    $cfca9a91faa35995$var$renderDropStat($cfca9a91faa35995$var$statDup, msg.duplicates ?? 0);
    $cfca9a91faa35995$var$renderDropStat($cfca9a91faa35995$var$statOld, msg.recvOldSong ?? 0);
    $cfca9a91faa35995$var$renderDropStat($cfca9a91faa35995$var$statParse, msg.recvBadParse ?? 0);
    $cfca9a91faa35995$var$renderDropStat($cfca9a91faa35995$var$statEvict, msg.evicted ?? 0);
    const queueDrops = msg.sdkQueueFull ?? 0;
    const audioQueueDrops = msg.sdkAudioQueueFull ?? 0;
    const metaQueueDrops = msg.sdkMetaQueueFull ?? 0;
    const queueDepth = msg.sdkQueueDepth ?? 0;
    const queueCapacity = msg.sdkQueueCapacity ?? 256;
    $cfca9a91faa35995$var$statSdkQueue.textContent = `audio ${audioQueueDrops} \xb7 meta ${metaQueueDrops} \xb7 ${queueDepth}/${queueCapacity}`;
    $cfca9a91faa35995$var$statSdkQueue.className = queueDrops > 0 ? "warn" : "";
    $cfca9a91faa35995$var$statSdkQueue.title = `all queue overflows ${queueDrops}; audio ${audioQueueDrops}; meta ${metaQueueDrops}; current audio queue depth ${queueDepth} of ${queueCapacity}`;
    const sdkErrors = msg.sdkErrors ?? 0;
    const reconnects = msg.sdkReconnects ?? 0;
    const filterErrors = msg.sdkInboundFilterErrors ?? 0;
    const duplicateDrops = msg.sdkDuplicateDropped ?? 0;
    const transportErrors = msg.sdkTransportErrors ?? 0;
    $cfca9a91faa35995$var$statSdkEvents.textContent = `err ${sdkErrors} \xb7 reconnect ${reconnects} \xb7 filter ${filterErrors} \xb7 dup ${duplicateDrops}`;
    $cfca9a91faa35995$var$statSdkEvents.className = sdkErrors + reconnects + filterErrors + duplicateDrops + transportErrors > 0 ? "warn" : "";
    $cfca9a91faa35995$var$statSdkEvents.title = `all SDK errors ${sdkErrors}; transport ${transportErrors}; inbound filter ${filterErrors}; reconnects ${reconnects}; overlap duplicates ${duplicateDrops}; empty delivery ${msg.sdkEmptyDeliveries ?? 0}; filter warnings ${msg.sdkFilterWarnings ?? 0}`;
    const decodeLatePackets = msg.decodeLatePackets ?? 0;
    const decodeForwardGaps = msg.decodeForwardGaps ?? 0;
    $cfca9a91faa35995$var$statDecodeOrder.textContent = `late ${decodeLatePackets} \xb7 forward ${decodeForwardGaps}`;
    $cfca9a91faa35995$var$statDecodeOrder.className = decodeLatePackets + decodeForwardGaps > 0 ? "warn" : "";
    if (msg.lastDiagnostic) {
        $cfca9a91faa35995$var$statLastDiagnostic.textContent = msg.lastDiagnostic;
        $cfca9a91faa35995$var$statLastDiagnostic.className = "warn";
    }
}
/**
 * @param {object} meta
 */ function $cfca9a91faa35995$var$renderMeta(meta) {
    $cfca9a91faa35995$var$stations = meta.stations || [];
    $cfca9a91faa35995$var$stationListEl.innerHTML = "";
    if ($cfca9a91faa35995$var$stations.length === 0) {
        $cfca9a91faa35995$var$stationListEl.innerHTML = "<li class=\"empty\">no stations in meta</li>";
        return;
    }
    for (const station of $cfca9a91faa35995$var$stations){
        const li = document.createElement("li");
        const btn = document.createElement("button");
        btn.type = "button";
        btn.className = station.online ? "" : "offline";
        if (station.id === $cfca9a91faa35995$var$selectedStationId) btn.classList.add("active");
        const lock = station.protected ? " \uD83D\uDD12" : "";
        btn.textContent = `${station.name}${lock}`;
        btn.disabled = !station.online;
        btn.addEventListener("click", ()=>$cfca9a91faa35995$var$pickStation(station.id));
        li.appendChild(btn);
        $cfca9a91faa35995$var$stationListEl.appendChild(li);
    }
    $cfca9a91faa35995$var$refreshNowPlayingCard();
    if ($cfca9a91faa35995$var$selectedStationId === null && $cfca9a91faa35995$var$metaConnected) $cfca9a91faa35995$var$pickStationFromUrl();
}
/**
 * Stores stream parameters from meta snapshot.
 * @param {object} meta
 */ function $cfca9a91faa35995$var$updateWireMeta(meta) {
    if (!meta) return;
    if (meta.sampleRate) {
        $cfca9a91faa35995$var$wireMeta.sampleRate = meta.sampleRate;
        $cfca9a91faa35995$var$metaSampleRate = meta.sampleRate;
    }
    if (meta.frameMs) $cfca9a91faa35995$var$wireMeta.frameMs = meta.frameMs;
    if (meta.channels) $cfca9a91faa35995$var$wireMeta.channels = meta.channels;
    if (meta.bitrate) $cfca9a91faa35995$var$wireMeta.bitrate = meta.bitrate;
}
/**
 * Channel label for UI.
 * @param {number} ch
 */ function $cfca9a91faa35995$var$formatChannelsLabel(ch) {
    if (ch <= 1) return "mono";
    if (ch === 2) return "stereo";
    return `${ch} ch`;
}
/**
 * Bitrate label for UI.
 * @param {number} bps
 */ function $cfca9a91faa35995$var$formatBitrateLabel(bps) {
    if (!bps) return "\u2014";
    if (bps >= 1000) return `${Math.round(bps / 1000)} kbps`;
    return `${bps} bps`;
}
/**
 * Stream quality line: Opus, bitrate, Hz, channels, frame_ms.
 * @param {object} [station]
 */ function $cfca9a91faa35995$var$formatWireStream(station) {
    const parts = [
        "Opus",
        $cfca9a91faa35995$var$formatBitrateLabel($cfca9a91faa35995$var$wireMeta.bitrate),
        `${$cfca9a91faa35995$var$wireMeta.sampleRate} Hz`,
        $cfca9a91faa35995$var$formatChannelsLabel($cfca9a91faa35995$var$wireMeta.channels),
        `${$cfca9a91faa35995$var$wireMeta.frameMs} ms`
    ];
    if (station?.protected) parts.push("AES");
    return parts.join(" \xb7 ");
}
/**
 * Updates stream quality under track and in stats.
 * @param {object} [station]
 */ function $cfca9a91faa35995$var$updateStreamDisplay(station) {
    const st = station ?? $cfca9a91faa35995$var$stations.find((s)=>s.id === $cfca9a91faa35995$var$selectedStationId) ?? null;
    if ($cfca9a91faa35995$var$selectedStationId === null) {
        $cfca9a91faa35995$var$streamInfoEl.textContent = "\u2014";
        $cfca9a91faa35995$var$streamInfoEl.hidden = true;
        $cfca9a91faa35995$var$statWire.textContent = $cfca9a91faa35995$var$formatWireStream(null);
        return;
    }
    const text = $cfca9a91faa35995$var$formatWireStream(st);
    $cfca9a91faa35995$var$streamInfoEl.textContent = text;
    $cfca9a91faa35995$var$streamInfoEl.hidden = false;
    $cfca9a91faa35995$var$statWire.textContent = text;
}
/**
 * Updates progress bar from position and duration.
 * position from stats (~480 ms) or meta (~1 s).
 * @param {number} positionMs
 * @param {number} [durationMs]
 * @param {number} [songId]
 */ function $cfca9a91faa35995$var$updateProgressBar(positionMs, durationMs = 0, songId = 0) {
    if (songId > 0 && songId !== $cfca9a91faa35995$var$progressSongId) {
        $cfca9a91faa35995$var$progressSongId = songId;
        $cfca9a91faa35995$var$progressDurationMs = 0;
    }
    if (durationMs > 0) $cfca9a91faa35995$var$progressDurationMs = durationMs;
    const denom = Math.max($cfca9a91faa35995$var$progressDurationMs, positionMs + 1000, 60000);
    const pct = Math.min(100, Math.max(0, positionMs / denom * 100));
    $cfca9a91faa35995$var$progressBar.style.width = `${pct}%`;
}
/**
 * Merges fresh meta fields into local station list.
 * @param {object} station
 */ function $cfca9a91faa35995$var$mergeStationMeta(station) {
    const idx = $cfca9a91faa35995$var$stations.findIndex((s)=>Number(s.id) === Number(station.id));
    if (idx >= 0) $cfca9a91faa35995$var$stations[idx] = station;
}
/**
 * Updates now playing card for selected station.
 */ function $cfca9a91faa35995$var$refreshNowPlayingCard() {
    if ($cfca9a91faa35995$var$selectedStationId === null) return;
    const station = $cfca9a91faa35995$var$stations.find((s)=>Number(s.id) === Number($cfca9a91faa35995$var$selectedStationId));
    if (station) $cfca9a91faa35995$var$updateNowPlaying(station);
}
/**
 * @param {object} station
 */ function $cfca9a91faa35995$var$updateNowPlaying(station) {
    $cfca9a91faa35995$var$titleEl.textContent = station.title || "\u2014";
    $cfca9a91faa35995$var$artistEl.textContent = station.artist || "\u2014";
    $cfca9a91faa35995$var$updateStreamDisplay(station);
    const songChanged = station.songId > 0 && station.songId !== $cfca9a91faa35995$var$displayedSongId;
    if (songChanged) {
        $cfca9a91faa35995$var$displayedSongId = station.songId;
        $cfca9a91faa35995$var$coverEl.removeAttribute("src");
    }
    if (station.coverUrl) {
        $cfca9a91faa35995$var$coverEl.src = station.coverUrl;
        $cfca9a91faa35995$var$coverEl.hidden = false;
        $cfca9a91faa35995$var$coverPlaceholder.hidden = true;
    } else {
        $cfca9a91faa35995$var$coverEl.hidden = true;
        $cfca9a91faa35995$var$coverPlaceholder.hidden = false;
    }
    $cfca9a91faa35995$var$updateProgressBar(station.positionMs, station.durationMs, station.songId);
}
/**
 * Request autoplay after a station is selected.
 */ function $cfca9a91faa35995$var$requestAutoplay() {
    $cfca9a91faa35995$var$pendingAutoplay = true;
    $cfca9a91faa35995$var$tryAutoplay();
}
/**
 * Start playback when a station is selected and meta is ready.
 */ async function $cfca9a91faa35995$var$tryAutoplay() {
    if (!$cfca9a91faa35995$var$pendingAutoplay || $cfca9a91faa35995$var$audioStarted || $cfca9a91faa35995$var$autoplayInFlight) return;
    if ($cfca9a91faa35995$var$selectedStationId === null || !$cfca9a91faa35995$var$metaConnected) return;
    if ($cfca9a91faa35995$var$pendingProtectedStation || $cfca9a91faa35995$var$passwordDialog.open) return;
    $cfca9a91faa35995$var$autoplayInFlight = true;
    await $cfca9a91faa35995$var$onPlay();
    $cfca9a91faa35995$var$autoplayInFlight = false;
    if ($cfca9a91faa35995$var$audioStarted) {
        $cfca9a91faa35995$var$pendingAutoplay = false;
        return;
    }
    $cfca9a91faa35995$var$setStatus("click Play to start", true, "warn");
}
/**
 * Station pick from the list.
 * @param {number|object} stationOrId
 */ function $cfca9a91faa35995$var$pickStation(stationOrId) {
    const id = typeof stationOrId === "object" ? stationOrId.id : stationOrId;
    const alreadyPlaying = $cfca9a91faa35995$var$audioStarted && !$cfca9a91faa35995$var$paused && Number($cfca9a91faa35995$var$selectedStationId) === Number(id);
    $cfca9a91faa35995$var$selectedStationId = id;
    $cfca9a91faa35995$var$refreshNowPlayingCard();
    $cfca9a91faa35995$var$renderMeta({
        stations: $cfca9a91faa35995$var$stations
    });
    const station = $cfca9a91faa35995$var$stations.find((s)=>Number(s.id) === Number(id));
    if (!station) return;
    if (alreadyPlaying) return;
    if (station.protected) {
        const remembered = $cfca9a91faa35995$var$stationPasswords.get(Number(station.id));
        if (remembered) {
            $cfca9a91faa35995$var$sendPickStation(station, remembered);
            $cfca9a91faa35995$var$requestAutoplay();
            return;
        }
        $cfca9a91faa35995$var$pendingProtectedStation = station;
        $cfca9a91faa35995$var$passwordHint.textContent = station.passwordHint ? `Hint: ${station.passwordHint}` : "Enter password";
        $cfca9a91faa35995$var$passwordInput.value = "";
        $cfca9a91faa35995$var$passwordDialog.showModal();
        return;
    }
    $cfca9a91faa35995$var$sendPickStation(station);
}
/**
 * @param {object} station
 * @param {string} [password]
 */ function $cfca9a91faa35995$var$sendPickStation(station, password) {
    if ($cfca9a91faa35995$var$audioStarted) $cfca9a91faa35995$var$worker?.postMessage({
        type: "selectStation",
        station: station,
        password: password
    });
    else $cfca9a91faa35995$var$worker?.postMessage({
        type: "pickStation",
        station: station,
        password: password
    });
}
/**
 * @param {Event} event
 */ function $cfca9a91faa35995$var$onPasswordSubmit(event) {
    event.preventDefault();
    const station = $cfca9a91faa35995$var$pendingProtectedStation;
    if (!station) {
        $cfca9a91faa35995$var$passwordDialog.close();
        return;
    }
    const password = $cfca9a91faa35995$var$passwordInput.value.trim();
    $cfca9a91faa35995$var$passwordDialog.close();
    if (password) $cfca9a91faa35995$var$stationPasswords.set(Number(station.id), password);
    $cfca9a91faa35995$var$sendPickStation(station, password);
    $cfca9a91faa35995$var$pendingProtectedStation = null;
}
function $cfca9a91faa35995$var$pickStationFromUrl() {
    const params = new URLSearchParams(window.location.search);
    const fromQuery = params.get("station");
    if (!fromQuery || !$cfca9a91faa35995$var$stations.length) return;
    const id = Number(fromQuery);
    const station = $cfca9a91faa35995$var$stations.find((s)=>s.id === id);
    if (station?.online) $cfca9a91faa35995$var$pickStation(station);
}
/**
 * FastPubSub indicator in header.
 * @param {"connecting"|"meta"|"audio"|"error"} state
 * @param {string} label
 */ function $cfca9a91faa35995$var$setFpsConnection(state, label) {
    $cfca9a91faa35995$var$fpsConnectionState = state;
    $cfca9a91faa35995$var$fpsConnectionDetail = label;
    $cfca9a91faa35995$var$refreshFpsHeader();
}
function $cfca9a91faa35995$var$refreshFpsHeader() {
    $cfca9a91faa35995$var$fpsStatusEl.className = `fps-status state-${$cfca9a91faa35995$var$fpsConnectionState}`;
    const edge = $cfca9a91faa35995$var$selectedEdgeLabel ? `${$cfca9a91faa35995$var$selectedEdgeLabel} \xb7 ` : "";
    const suffix = $cfca9a91faa35995$var$linkQualityLabel ? ` \xb7 ${$cfca9a91faa35995$var$linkQualityLabel}` : "";
    $cfca9a91faa35995$var$fpsLabelEl.textContent = `FastPubSub: ${edge}${$cfca9a91faa35995$var$fpsConnectionDetail}${suffix}`;
}
/**
 * @param {string} text
 * @param {boolean} ok
 * @param {"warn"|undefined} tone
 */ function $cfca9a91faa35995$var$setStatus(text, ok, tone) {
    $cfca9a91faa35995$var$statusEl.textContent = text;
    $cfca9a91faa35995$var$statusEl.className = "status";
    if (ok) $cfca9a91faa35995$var$statusEl.classList.add("ok");
    if (tone === "warn") $cfca9a91faa35995$var$statusEl.classList.add("warn");
}
$cfca9a91faa35995$var$coverEl.addEventListener("error", ()=>{
    $cfca9a91faa35995$var$coverEl.hidden = true;
    $cfca9a91faa35995$var$coverPlaceholder.hidden = false;
});
$cfca9a91faa35995$var$boot();


//# sourceMappingURL=radio.230252e7.js.map
